import { mergeClasses, Textarea } from "@fluentui/react-components";
import { RecordStop16Regular, Send16Regular } from "@fluentui/react-icons";
import { ALLOWED_FILE_EXTENSIONS, ConstantValues } from "common/constants";
import { useResizer } from "hooks/useResizer";
import { FileWithId } from "models/FileWithId";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { setCurrentFiles } from "../../../redux/files";
import {
  useButtonStyles,
  useLayoutStyles,
  useStaticStyles,
} from "./MessageInput.styles";
import { IMessageInputProps } from "./MessageInput.types";

export const MessageInput = (props: IMessageInputProps) => {
  useStaticStyles();
  const buttonStyles = useButtonStyles();
  const layoutStyles = useLayoutStyles();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isMobile = useResizer();
  const isSendDisabled = !props.text || props.text?.trim() === "";

  const [isDraggingOver, setIsDraggingOver] = useState<boolean>(false);
  const dispatch = useDispatch();

  const handlePaste = (
    event: ClipboardEvent,
    setUploadFiles: React.Dispatch<React.SetStateAction<FileWithId[]>>,
    dispatch: any,
  ) => {
    const clipboardData = event.clipboardData;
    const files: File[] = clipboardData?.files
      ? Array.from(clipboardData.files)
      : [];

    if (files.length) {
      const fileWithIds = files.map((file) => {
        let fileName = file.name;
        const fileExtension = fileName.substring(fileName.lastIndexOf("."));
        const baseName = fileName.substring(0, fileName.lastIndexOf("."));
        let counter = 1;

        setUploadFiles((prevFiles) => {
          const existingFileNames = prevFiles.map(
            (existingFile) => existingFile.file.name,
          );
          while (existingFileNames.includes(fileName)) {
            counter++;
            fileName = `${baseName} (${counter})${fileExtension}`;
          }
          return prevFiles;
        });

        return { file: new File([file], fileName), id: uuidv4() };
      });

      setUploadFiles((prevFiles) => [...prevFiles, ...fileWithIds]);
      dispatch(
        setCurrentFiles(fileWithIds.map((fileWithId) => fileWithId.file.name)),
      );
    }
  };

  useEffect(() => {
    const textArea: HTMLElement | null = document.getElementById(props.id);
    const pasteHandler = (event: ClipboardEvent) =>
      handlePaste(event, props.setUploadFiles, dispatch);
    if (textArea) {
      textArea.addEventListener("paste", pasteHandler);
    }

    return () => {
      if (textArea) {
        textArea.removeEventListener("paste", pasteHandler);
      }
    };
  }, [props.id]);

  useEffect(() => {
    if (props.disableDragDrop) {
      return;
    }

    const textarea: HTMLElement | null = document.getElementById(props.id);
    if (textarea) {
      textarea.addEventListener("dragenter", handleDragEnter);
      textarea.addEventListener("dragover", handleDragOver);
      textarea.addEventListener("dragleave", handleDragLeave);
      textarea.addEventListener("drop", handleDrop);
    }

    return () => {
      if (textarea) {
        textarea.removeEventListener("dragenter", handleDragEnter);
        textarea.removeEventListener("dragover", handleDragOver);
        textarea.removeEventListener("dragleave", handleDragLeave);
        textarea.removeEventListener("drop", handleDrop);
      }
    };
  }, [props.id, props.disableDragDrop]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "0";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [props.text]);

  const handleDragEnter = (event: DragEvent) => {
    event.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (event: DragEvent) => {
    event.preventDefault();
    setIsDraggingOver(false);
  };

  const handleDrop = (event: DragEvent) => {
    event.preventDefault();
    setIsDraggingOver(false);

    const files = Array.from(event.dataTransfer?.files || []);
    if (!files.length) return;

    const allowedExtensions = ALLOWED_FILE_EXTENSIONS.map((ext) =>
      ext.toLowerCase(),
    );
    const fileWithIds: FileWithId[] = [];

    files.forEach((file) => {
      const fileExtension = file.name.split(".").pop()?.toLowerCase();

      if (fileExtension === "pptx" || fileExtension === "ppt") {
        props.setUnsupportedFileMessage(
          "PPT/PPTX files are not supported. Please save the file as a PDF and then upload the PDF.",
        );
        return;
      }

      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        props.setUnsupportedFileMessage(
          `The file "${file.name}" is not supported by AIMS. Only these file types are allowed: ${ALLOWED_FILE_EXTENSIONS.join(", ")}`,
        );
        return;
      }

      fileWithIds.push({ file, id: uuidv4() });
    });

    if (fileWithIds.length > 0) {
      props.setUploadFiles((prevFiles) => {
        const existingFileNames = prevFiles.map(
          (existingFile) => existingFile.file.name,
        );
        const newFiles = fileWithIds.filter(
          (fileWithId) => !existingFileNames.includes(fileWithId.file.name),
        );
        dispatch(setCurrentFiles(newFiles.map((file) => file.file.name)));
        return [...prevFiles, ...newFiles];
      });
    }
  };

  const onChange = (_e: any, data: any) => {
    props.setText(data.value);
    localStorage.setItem(ConstantValues.DRAFT_MESSAGE, data.value);
  };

  return (
    <>
      {isDraggingOver && (
        <div className={layoutStyles.dragOverlay}>Drag and drop files here</div>
      )}

      <Textarea
        id={props.id}
        placeholder="How can I help you today?"
        resize="none"
        ref={textareaRef}
        className={`aims-chat-textarea ${mergeClasses(
          buttonStyles.textArea,
          isMobile && buttonStyles.textAreaMobile,
          !props.text && buttonStyles.textAreaEmpty,
        )}`}
        aria-label="Chat message"
        textarea={{
          className: mergeClasses(
            layoutStyles.innerTextarea,
            props.disabled && layoutStyles.innerTextareaBusy,
          ),
        }}
        value={props.text ?? ConstantValues.EMPTY_STRING}
        onChange={onChange}
        readOnly={props.disabled}
        aria-disabled={props.disabled || undefined}
        onKeyDown={props.onKeyPress}
      />
      {props.disabled ? (
        <button
          onClick={props.onAbort}
          className={mergeClasses(
            buttonStyles.stopButton,
            isMobile && buttonStyles.stopButtonMobile,
          )}
          aria-label="Stop generating"
          title="Stop generating"
        >
          <RecordStop16Regular />
        </button>
      ) : (
        <button
          disabled={isSendDisabled}
          onClick={props.onSend}
          className={mergeClasses(
            buttonStyles.sendButton,
            isMobile && buttonStyles.sendButtonMobile,
            isSendDisabled && buttonStyles.sendButtonDisabled,
          )}
          aria-label="Send message"
          title="Send message"
        >
          <Send16Regular style={{ transform: "rotate(-90deg)" }} />
        </button>
      )}
    </>
  );
};
